<template>
  <div
    :id="elementId"
    :data-rotate="rotateAngle"
    :data-elementindex="getElementIndex"
    :style="getStyles"
    :data-translateX="parseInt(width)"
    :data-translateY="parseInt(height)"
    :class="getClass"
    :data-subtype="objectProps.type"
    :data-scalex="objectProps.scaleX"
    :data-scaley="objectProps.scaleY"
    class="position-absolute d-flex image-container"
    data-type="video">
    <video
      :startTime="objectProps.videoDetails.startTime"
      :endTime="objectProps.videoDetails.endTime"
      :style="getImageStyles"
      :id="'video'+elementId"
      :src="`${objectProps.videoDetails.videoHighRes}#t=${objectProps.videoDetails.startTime}`"
      :data-highres="`${objectProps.videoDetails.videoHighRes}`"
      preload
      onloadstart="this.volume=1">
      <source
        :src="`${objectProps.videoDetails.videoHighRes}#t=${objectProps.videoDetails.startTime}`"
        :type="`${objectProps.videoDetails.mimeType}`"></source>
    </video>
    <div
      :style="getTintStyle"
      class="tint-div"/>
    <div
      :style="getVignetteStyle"
      class="vignette-div" />
  </div>
</template>
<script>
import EventBus from '@/EventBus';
import ApplicationEventTypes from '@/mixins/ApplicationEventTypes';

export default {
  name:  'VideoComponent',
  props: {
    objectProps: {
      type:    Object,
      default: {},
    },
    elementIndex: {
      type:    Number,
      default: 0,
    },
    isRenderingPagePanel: {
      type:    Boolean,
      default: false,
    },
    displayControls: {
      type:    Boolean,
      default: false,
    },
  },
  computed: {
    getAnimationStyle() {
      return !!((this.objectProps.animation.style.includes('photoZom') || this.objectProps.animation.style.includes('photoRise') || this.objectProps.animation.style.includes('photoFlow')));
    },
    getOverflow() {
      return (this.objectProps.isAnimated && this.getAnimationStyle) ? 'hidden' : 'visible';
    },
    getTintStyle() {
      let toReturn       = { display: 'none' };
      const borderRadius = (this.objectProps.croppedSrc !== 'none' && this.objectProps.circleCrop) ? '50%' : '0px';
      try {
        const { effects } = this;
        if (effects.tint.value > 0) {
          toReturn = {
            display:         'block', backgroundColor: effects.tint.color, opacity:         effects.tint.value / 100, borderRadius,
          };
        }
      } catch (ex) {
        console.log('Error in processing tint style @getTintStyle', ex.message);
      } finally {
        return toReturn;
      }
    },
    getVignetteStyle() {
      const borderRadius = (this.objectProps.croppedSrc !== 'none' && this.objectProps.circleCrop) ? '50%' : '0px';
      try {
        return {
          position: 'absolute', width:    '100%', top:      '0', bottom:   '0', overflow: 'hidden', borderRadius,
        };
      } catch (ex) {
        console.log('Error in processing tint style @getTintStyle', ex.message);
        return {
          position: 'absolute', width:    '100%', top:      '0', bottom:   '0', overflow: 'hidden',
        };
      }
    },
    elementId:   ({ objectProps }) => objectProps.Id,
    width:       ({ objectProps }) => objectProps.Width,
    height:      ({ objectProps }) => objectProps.Height,
    transformX:  ({ objectProps }) => objectProps.X,
    transformY:  ({ objectProps }) => objectProps.Y,
    rotateAngle: ({ objectProps }) => objectProps.RotateDegree,
    sortOrder:   ({ objectProps }) => objectProps.PdfSortOrder,
    effects:     ({ objectProps }) => objectProps.effects,
    isVisible:   ({ objectProps }) => objectProps.IsVisible,
    isHFlipped:  ({ objectProps }) => objectProps.IsHFlipped,
    isVFlipped:  ({ objectProps }) => objectProps.IsVFlipped,
    imageURL() {
      let url = this.objectProps.croppedSrc === 'none' ? this.objectProps.DefaultImageUrl : this.objectProps.croppedSrc;
      if (url === undefined) {
        url = this.objectProps.AlternativeImageUrls[0];
      }
      return url;
    },
    getLocked() {
      return this.objectProps.locked;
    },
    getImageType() {
      return this.objectProps.ImageType;
    },
    getSvgContent() {
      return this.objectProps.SvgProperty.content;
    },
    imageURLHighResPath() {
      // console.log(this.objectProps.HighResPath)
      if (this.objectProps.HighResPath && this.objectProps.HighResPath !== 'undefined') {
        if (this.objectProps.croppedHighResPath !== 'none' && this.objectProps.croppedHighResPath != 'undefined') {
          return this.objectProps.croppedHighResPath;
        }

        return this.objectProps.HighResPath;
      }
      if (this.objectProps.croppedSrc !== 'none') { return this.objectProps.croppedSrc; }
      return this.objectProps.DefaultImageUrl;
    },
    getShapeStyles() {
      const {
        X, Y, RotateDegree, scaleX, scaleY, Width, Height, PdfSortOrder, Alpha = 100,
      } = this.objectProps;
      return {
        transform:       `rotate(${RotateDegree}deg) scale(${scaleX},${scaleY})`,
        backgroundColor: this.getShapeType === 'rectangle' ? this.objectProps.SvgProperty.DefaultColor : '',
        height:          Height !== 0 ? `${Height}px` : 'auto',
        width:           Width !== 0 ? `${Width}px` : 'auto',
        zIndex:          this.objectProps.PdfSortOrder,
        display:         this.objectProps.IsVisible ? 'block !important' : 'none !important',
        opacity:         parseFloat(Alpha) / 100,
        top:             `${Y}px`,
        left:            `${X}px`,
        // transformOrigin:'top left'
      };
    },
    getStyles() {
      const {
        vignette, transparency, brightness, saturation, contrast, blur,
      } = this.effects;
      const basicImageProperties = {
        transform:       this.getTransformCSS,
        top:             `${this.transformY}px`,
        left:            `${this.transformX}px`,
        height:          `${this.height}px`,
        width:           `${this.width}px`,
        zIndex:          this.sortOrder,
        visibility:      this.isVisible ? 'visible' : 'hidden',
        opacity:         `${transparency.value / 100}`,
        transformOrigin: 'inherit',
        overflow:        this.getOverflow,
      };
      this.objectProps.Name != 'z-border' && (delete basicImageProperties.transformOrigin);
      basicImageProperties.opacity == '1' && (delete basicImageProperties.opacity);
      let filterProperties = {
        filter:            this.getFilterCSS,
        '--vignetteSize':  `${500 - (4 * vignette.value)}%`,
        '--vignetteColor': `rgba(0,0,0,${vignette.value / 100})`,
      };

      if (vignette.value == 0) {
        delete filterProperties['--vignetteSize'];
        delete filterProperties['--vignetteColor'];
      }
      if (brightness.value == 50 && saturation.value == 50 && contrast.value == 50 && blur.value == 0) {
        delete filterProperties.filter;
      }
      const srcExtension = this.imageURL.substr(parseInt(this.imageURL.lastIndexOf('.')) + 1, this.imageURL.length - 1);
      srcExtension.trim() === 'svg' && (filterProperties = {});
      return Object.assign({}, basicImageProperties, filterProperties);
    },
    getImageStyles() {
      return {
        transform: `scale(${this.isHFlipped === true ? -1 : 1},${this.isVFlipped === true ? -1 : 1})`,
        objectFit: 'fill',
      };
    },

    getTransformCSS() {
      const { rotateAngle } = this;
      return `scale(${this.objectProps.scaleX},${this.objectProps.scaleY}) rotate(${rotateAngle}deg)`;
    },
    getFilterCSS() {
      const {
        brightness, saturation, contrast, blur,
      } = this.effects;
      return `brightness(${brightness.value * 2}%) saturate(${saturation.value * 2}%) contrast(${contrast.value * 2}%) blur(${blur.value / 10}px)`;
    },
    getElementIndex() {
      return this.objectProps.elementIndex;
    },
    getClass() {
      return this.objectProps.locked ? 'untarget' : 'target';
    },
  },
  created() {
    if (!this.objectProps.HighResV2Height || !this.objectProps.HighResV2Width) {
      this.loadHighResImage();
    }

    if (!this.objectProps.DefaultImageHeight || !this.objectProps.DefaultImageWidth) {
      this.loadDefaultImage();
    }
  },
  beforeMount() {

  },
  mounted() {
    if (this.objectProps.ImageType == 'SVGImage') {
      if (this.$refs.svgDiv.getElementsByTagName('style').length > 0) {
        this.convertClassToInline();
        this.$refs.svgDiv.getElementsByTagName('style')[0].remove();
      }
      this.setSvgColor();
    }
  },
  updated() {
    EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.UPDATED);
  },
  methods: {
    loadDefaultImage() {
      const defaultImageUrl  = new Image();
      defaultImageUrl.onload = () => {
        !this.objectProps.DefaultImageHeight && (this.objectProps.DefaultImageHeight = defaultImageUrl.height);
        !this.objectProps.DefaultImageWidth && (this.objectProps.DefaultImageWidth  = defaultImageUrl.width);
      };
      defaultImageUrl.src    = this.imageURL;
    },
    loadHighResImage() {
      const highResImage  = new Image();
      highResImage.onload = () => {
        !this.objectProps.HighResV2Height && (this.objectProps.HighResV2Height = highResImage.height);
        !this.objectProps.HighResV2Width && (this.objectProps.HighResV2Width = highResImage.width);
      };
      highResImage.src    = this.objectProps.HighResPath;
    },
    setSvgColor() {
      // const svgElement = document.getElementById(this.objectProps.Id).firstChild.firstChild;
      const svgElement = document.querySelector(`[id='${this.objectProps.Id}'] svg`);
      const paths      = svgElement.getElementsByTagName('path');
      for (let i = 0; i < paths.length; i++) {
        if (paths[i].style.fill !== '') {
          this.objectProps.SvgProperty.DefaultColor = paths[i].style.fill;
          break;
        }
      }
    },
    convertClassToInline() {
      const svgElement = document.getElementById(this.objectProps.Id).firstChild.firstChild;
      const paths      = svgElement.getElementsByTagName('path');
      for (let i = 0; i < paths.length; i++) {
        const { fill }      = getComputedStyle(paths[i]);
        paths[i].style.fill = fill;
      }
    },
  },
};

</script>
<style lang="scss" scoped>
div.image-container .vignette-div:after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
  width: var(--vignetteSize);
  height: var(--vignetteSize);;
  background: radial-gradient(ellipse at center, #00000000 0%, var(--vignetteColor) 100%);
}

div.tint-div {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.svg-height{
  height: inherit
}
video{
  width:100%;
  height:100%;
}
</style>
